import { useTranslation } from "react-i18next";
import type { HistoryPeriodResponse, MyConsumptionResponse } from "../../api/reporting";
import { formatDate, formatMoney } from "./formatters";
import { comparisonArrow, comparisonSentence } from "./monthComparisonText";
import { computeUsagePace, consolidatedPaceInput, formatMonthName, monthChange, projectMonthEnd, type UsagePace } from "./usagePace";
import styles from "./UsageHero.module.css";

/**
 * The top of "My Usage": the consolidated figure, an allowance meter showing spend so far, the
 * projection and today's place in the month, a one-line pace summary, and a column of key figures
 * (headroom per working day, projected month end, average per day and the change against last
 * month). The meter, summary, headroom and projection are current-month only, and the meter,
 * summary and headroom also need a positive USD allowance and a USD total, since the allowance
 * cannot be compared with any other currency.
 *
 * @param props.response - The loaded consumption response; must carry a consolidated figure.
 * @param props.effectiveAllowanceUsd - The person's effective allowance in USD, or null when there is none.
 * @param props.isCurrentMonth - Whether the response is for the current month.
 * @param props.previousPeriod - The month immediately before this one from the history feed, or null.
 * @param props.subjectName - Who the figures belong to, when someone else is looking at them (a manager); the words then say
 * "their" rather than "your". Absent on the person's own page.
 */
export function UsageHero({
  response,
  effectiveAllowanceUsd,
  isCurrentMonth,
  previousPeriod,
  subjectName,
}: {
  response: MyConsumptionResponse;
  effectiveAllowanceUsd: number | null;
  isCurrentMonth: boolean;
  previousPeriod: HistoryPeriodResponse | null;
  subjectName?: string;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  // The "they" context picks the third-person wording for a manager looking at someone else's figures.
  const context = subjectName !== undefined ? "they" : undefined;
  const consolidated = response.consolidated;
  const input = consolidatedPaceInput(response);
  if (!consolidated || !input) {
    return null;
  }

  const currency = consolidated.currency;
  const allowance =
    isCurrentMonth && currency === "USD" && effectiveAllowanceUsd !== null && effectiveAllowanceUsd > 0 ? effectiveAllowanceUsd : null;
  const pace = allowance !== null ? computeUsagePace(input, allowance) : null;
  const projected = isCurrentMonth ? projectMonthEnd(input) : null;

  const previousTotal = previousPeriod?.currency === currency ? previousPeriod.totalAmount : null;
  const change = monthChange(projected ?? consolidated.amount, previousTotal);
  const previousMonth = previousPeriod ? formatMonthName(previousPeriod.periodStart) : "";
  const basis = isCurrentMonth ? "projected" : "actual";

  return (
    <section className={styles.hero} data-spotlight aria-label={t("consumption.consolidated.title")}>
      <div className={styles.main}>
        <div className={styles.headerRow}>
          <p className={`panel-title ${styles.eyebrow}`}>{t("consumption.hero.eyebrow", { context, name: subjectName })}</p>
          <p className={styles.periodRange}>
            {t("consumption.period.range", { start: formatDate(response.periodStart), end: formatDate(response.periodEnd) })}
          </p>
        </div>

        <p className={styles.amount}>
          <span>{formatMoney(consolidated.amount, currency)}</span>
          {isCurrentMonth && effectiveAllowanceUsd !== null && (
            <span className={styles.allowanceTotal}>
              {" / "}
              {formatMoney(effectiveAllowanceUsd, "USD")}
            </span>
          )}
        </p>

        {pace && allowance !== null && <AllowanceMeter pace={pace} allowance={allowance} context={context} />}
        {pace && allowance !== null && <PaceSummary pace={pace} amount={consolidated.amount} allowance={allowance} context={context} />}
        {!consolidated.isFullyConverted && <p className={styles.note}>{t("consumption.consolidated.notFullyConverted")}</p>}
      </div>

      <dl className={styles.stats} aria-label={t("consumption.hero.statsLabel")}>
        {pace && pace.remaining > 0 && (
          <div className={`${styles.stat} ${styles.statFeatured}`}>
            <dt>{t("consumption.hero.headroomLabel")}</dt>
            <dd>{formatMoney(pace.headroomPerWorkingDay, "USD")}</dd>
          </div>
        )}
        {projected !== null && (
          <div className={styles.stat}>
            <dt>{t("consumption.hero.projectedLabel")}</dt>
            <dd className={allowance !== null && projected > allowance ? styles.overAllowance : undefined}>{formatMoney(projected, currency)}</dd>
          </div>
        )}
        <div className={styles.stat}>
          <dt>{t("consumption.hero.averageLabel")}</dt>
          <dd>
            {formatMoney(consolidated.burnRatePerDay, currency)}
            <span className={styles.statNote}>{t("consumption.hero.averageBasis", { days: consolidated.burnRateBasisDays })}</span>
          </dd>
        </div>
        {change && (
          // The figures sit two to a row; when this one would be left alone on its row, it spans it.
          <div className={projected === null ? styles.stat : `${styles.stat} ${styles.statWide}`}>
            <dt>
              {basis === "projected"
                ? t("consumption.hero.comparisonProjectedLabel", { month: previousMonth })
                : t("consumption.hero.comparisonActualLabel", { month: previousMonth })}
            </dt>
            <dd>
              <span aria-hidden="true">
                {change.direction === "same" ? t("consumption.hero.comparisonSameValue") : `${comparisonArrow(change) ?? ""} ${String(change.percent)}%`}
              </span>
              <span className="visually-hidden">{comparisonSentence(t, change, basis, previousMonth)}</span>
            </dd>
          </div>
        )}
      </dl>
    </section>
  );
}

/**
 * A bar from zero to the allowance: solid for spend so far, hatched out to the projected month end,
 * and a tick at today's place in the month, so "ahead of pace" reads as the solid bar passing the
 * tick. Exposed to assistive technology as a meter of the percentage used.
 *
 * @param props.pace - The pace figures for the period.
 * @param props.allowance - The allowance in USD.
 */
function AllowanceMeter({ pace, allowance, context }: { pace: UsagePace; allowance: number; context: "they" | undefined }): React.JSX.Element {
  const { t } = useTranslation();
  const usedPercent = Math.min(100, pace.percentUsed);
  const projectedPercent = Math.min(100, (pace.projectedMonthEnd / allowance) * 100);
  const overPace = pace.projectedMonthEnd > allowance;
  const roundedUsed = Math.round(pace.percentUsed);

  return (
    <div className={styles.meter}>
      <div
        className={styles.track}
        role="meter"
        aria-label={t("consumption.hero.meterLabel")}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.min(100, roundedUsed)}
        aria-valuetext={t("consumption.hero.meterValue", { percent: roundedUsed, context })}
      >
        <div className={`${styles.projectedFill} ${overPace ? styles.projectedOver : ""}`} style={{ width: `${String(projectedPercent)}%` }} />
        <div className={styles.usedFill} style={{ width: `${String(usedPercent)}%` }} />
        <div className={styles.todayMarker} style={{ left: `${String(Math.min(100, pace.percentElapsed))}%` }} />
      </div>
      <div className={styles.meterKey} aria-hidden="true">
        <span className={styles.keyItem}>
          <span className={`${styles.keySwatch} ${styles.keyUsed}`} />
          {t("consumption.hero.meterValue", { percent: roundedUsed, context })}
        </span>
        <span className={styles.keyItem}>
          <span className={`${styles.keySwatch} ${styles.keyProjected} ${overPace ? styles.projectedOver : ""}`} />
          {t("consumption.hero.meterProjected")}
        </span>
        <span className={styles.keyItem}>
          <span className={styles.keyToday} />
          {t("consumption.hero.meterToday")}
        </span>
      </div>
    </div>
  );
}

/**
 * The one-line pace summary: used up, on track, or over pace (stated in words, and in the warning
 * colour when over pace).
 *
 * @param props.pace - The pace figures for the period.
 * @param props.amount - The consolidated spend so far, in USD.
 * @param props.allowance - The allowance in USD.
 */
function PaceSummary({ pace, amount, allowance, context }: { pace: UsagePace; amount: number; allowance: number; context: "they" | undefined }): React.JSX.Element {
  const { t } = useTranslation();
  const used = String(Math.round(pace.percentUsed));
  const elapsed = String(Math.round(pace.percentElapsed));
  const projected = formatMoney(pace.projectedMonthEnd, "USD");
  const allowanceText = formatMoney(allowance, "USD");

  if (amount >= allowance) {
    return <p className={`${styles.summary} ${styles.summaryOverPace}`}>{t("consumption.summary.usedAll", { allowance: allowanceText, context })}</p>;
  }
  if (pace.projectedMonthEnd <= allowance) {
    return <p className={styles.summary}>{t("consumption.summary.onTrack", { used, elapsed, projected, context })}</p>;
  }
  return (
    <p className={`${styles.summary} ${styles.summaryOverPace}`}>
      {t("consumption.summary.overPace", { used, elapsed, projected, allowance: allowanceText, context })}
    </p>
  );
}
